import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { InlineLoadingState } from '../../LoadingSpinner';
import { PayrollAdjustmentSortableHeader } from './PayrollAdjustmentSortableHeader';
import type {
  PayrollAdjustmentSortState,
  PayrollAdjustmentTableSortColumn,
} from './payrollAdjustmentListShared';
import {
  formatLohnartDisplay,
  formatPayrollAdjustmentDate,
  formatPayrollAdjustmentEuro,
  type PayrollAdjustmentCategory,
  type PayrollAdjustmentMonthRow,
} from '../../../utils/payrollAdjustmentCategories';

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

type PayrollAdjustmentBookingsTableProps = {
  rows: PayrollAdjustmentMonthRow[];
  columns: Array<{ label: string; sortKey: PayrollAdjustmentTableSortColumn; align?: 'left' | 'right' }>;
  sort: PayrollAdjustmentSortState;
  onSort: (column: PayrollAdjustmentTableSortColumn) => void;
  categoryByKey: Map<string, PayrollAdjustmentCategory>;
  loading?: boolean;
  emptyMessage: string;
  showEmployeeColumn?: boolean;
  deleteRecurringRows?: boolean;
  onEditEntry: (entryId: string) => void;
  onEditRule: (ruleId: string) => void;
  onDeleteEntry: (entryId: string) => void;
  onDeleteRule: (ruleId: string) => void;
};

export function PayrollAdjustmentBookingsTable({
  rows,
  columns,
  sort,
  onSort,
  categoryByKey,
  loading = false,
  emptyMessage,
  showEmployeeColumn = false,
  deleteRecurringRows = false,
  onEditEntry,
  onEditRule,
  onDeleteEntry,
  onDeleteRule,
}: PayrollAdjustmentBookingsTableProps) {
  const colSpan = columns.length + 1;

  return (
    <div className="overflow-hidden rounded-lg border border-app-border bg-app-surface">
      {loading && rows.length === 0 ? (
        <InlineLoadingState label="Lade Be- und Abzüge…" className="py-16" />
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {columns.map((col) => (
                <PayrollAdjustmentSortableHeader
                  key={col.sortKey}
                  label={col.label}
                  column={col.sortKey}
                  sort={sort}
                  onSort={onSort}
                  align={col.align}
                />
              ))}
              <th
                scope="col"
                className="sticky top-0 z-10 w-16 border-b border-app-border bg-app-surface-muted px-3.5 py-2.5"
                aria-hidden
              />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-app-text-subtle">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const cat = categoryByKey.get(row.category_key);
                const isBezug = row.type === 'bezug';
                return (
                  <tr key={row.id} className="border-b border-app-border last:border-0">
                    {showEmployeeColumn ? (
                      <td className="px-3.5 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-app-surface-muted text-xs font-bold text-app-text-muted">
                            {initialsFromName(row.employee_name)}
                          </span>
                          <span className="font-medium text-app-text">{row.employee_name}</span>
                        </div>
                      </td>
                    ) : null}
                    <td className="whitespace-nowrap px-3.5 py-2.5 text-app-text-muted">
                      {formatPayrollAdjustmentDate(row.booking_date)}
                    </td>
                    <td className="px-3.5 py-2.5 text-app-text-muted">
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2 w-2 rounded-full ${isBezug ? 'bg-emerald-500' : 'bg-red-500'}`}
                        />
                        <span>{cat?.label ?? row.category_key}</span>
                        {row.recurring ? (
                          <span
                            className="rounded bg-app-surface-muted px-1.5 py-0.5 text-xs font-medium text-app-text-muted"
                            title="Aus regelmäßiger Buchung"
                          >
                            monatlich
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3.5 py-2.5 font-mono text-xs text-app-text-subtle">
                      {formatLohnartDisplay(cat?.lohnart ?? '')}
                    </td>
                    <td className="max-w-[280px] truncate px-3.5 py-2.5 text-xs text-app-text-subtle">
                      {row.note || '-'}
                    </td>
                    <td
                      className={`px-3.5 py-2.5 text-right font-semibold ${
                        isBezug ? 'text-emerald-600' : 'text-red-600'
                      }`}
                    >
                      {isBezug ? '+ ' : '− '}
                      {formatPayrollAdjustmentEuro(row.amount)}
                    </td>
                    <td className="px-3.5 py-2.5">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          title="Bearbeiten"
                          onClick={() =>
                            row.recurring && row.rule_id
                              ? onEditRule(row.rule_id)
                              : row.entry_id && onEditEntry(row.entry_id)
                          }
                          className="flex h-7 w-7 items-center justify-center rounded text-app-text-subtle hover:bg-app-hover"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {!row.recurring && row.entry_id ? (
                          <button
                            type="button"
                            title="Löschen"
                            onClick={() => onDeleteEntry(row.entry_id!)}
                            className="flex h-7 w-7 items-center justify-center rounded text-app-text-subtle hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : deleteRecurringRows && row.recurring && row.rule_id ? (
                          <button
                            type="button"
                            title="Regelmäßige Buchung löschen"
                            onClick={() => onDeleteRule(row.rule_id!)}
                            className="flex h-7 w-7 items-center justify-center rounded text-app-text-subtle hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
